import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import { Spin, Modal } from 'antd';
import './TrialAccountPage.css';

// 案内モーダルの見出し（【…】）のスタイル
const MODAL_HEADING_STYLE = {
  fontWeight: 'var(--font-bold)',
  marginBottom: 'var(--space-1)',
};

// 簡易ログイン完了時の案内モーダル本文を組み立てる
// resent: 既存の簡易アカウントへログイン情報を再送した場合は true
const buildQuickEntryNotice = (resent) => (
  <div>
    <p style={MODAL_HEADING_STYLE}>【現在の状態】</p>
    <p>仮ログイン（メールアドレス未確認）の状態でログインしています。</p>
    <p>
      {resent
        ? 'ログイン情報を再送しました。メールをご確認ください。'
        : 'ログイン情報（URL・email・Password）をメールでお送りしました。'}
    </p>

    <p style={MODAL_HEADING_STYLE}>【いまできること】</p>
    <ul>
      <li>アンケート・診断への回答</li>
      <li>動画の視聴</li>
    </ul>

    <p style={MODAL_HEADING_STYLE}>【いまはできないこと】</p>
    <ul>
      <li>結果（PDF）のダウンロード</li>
      <li>パスワードの変更</li>
    </ul>

    <p style={MODAL_HEADING_STYLE}>【結果を確認するには】</p>
    <p>メールに記載のパスワードで再ログインしてください。</p>
    <p>
      その後、氏名などの情報登録をしていただくと、結果の確認を含む全ての機能をご利用いただけます。
    </p>
  </div>
);

// 案内モーダルを表示し、OK を押したらトップページへ遷移する
// ※ Redux の user 状態・ナビバーを新しいセッションで再初期化するため
//   history.push ではなくページ遷移（replace）を使う
const showQuickEntryNotice = (resent) => {
  Modal.info({
    title: 'トライアルのご利用を開始しました',
    content: buildQuickEntryNotice(resent),
    okText: 'OK',
    width: 520,
    keyboard: false,
    maskClosable: false,
    onOk: () => window.location.replace('/'),
  });
};

// 通信エラー・想定外エラー時の共通メッセージ
const GENERIC_ERROR_MESSAGE =
  '通信エラーが発生しました。時間をおいて再度お試しください。';

// レートリミット（429）超過時のメッセージ
// ※ サーバーが JSON ではなく文字列を返す場合があるため固定文言で案内する
const RATE_LIMIT_MESSAGE =
  'アクセスが集中しています。しばらく時間をおいて、もう一度リンクを開いてください。';

// 例外オブジェクトから画面表示用のメッセージを決定する
const resolveErrorMessage = (error) => {
  const response = error && error.response;
  // ネットワークエラー等（レスポンス無し）
  if (!response) return GENERIC_ERROR_MESSAGE;
  // レートリミット超過（本文が JSON でない可能性があるため先に判定）
  if (response.status === 429) return RATE_LIMIT_MESSAGE;
  const data = response.data;
  // 入力値エラー（express-validator）
  if (data && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors[0].msg || GENERIC_ERROR_MESSAGE;
  }
  // サーバーエラー等
  if (data && typeof data.message === 'string' && data.message) {
    return data.message;
  }
  return GENERIC_ERROR_MESSAGE;
};

function TrialAccountPage(props) {
  // URLパラメータからメールアドレスとキャンペーンキーを取得
  const urlParams = new URLSearchParams(props.location.search);
  const emailFromUrl = urlParams.get('email') || '';
  const campaignKey = urlParams.get('key') || '';

  // 画面状態：エラー文言が入っていればエラー画面、空なら処理中画面
  // （成功時は画面遷移するため専用の状態は持たない）
  const [errorMessage, setErrorMessage] = useState('');

  // React 18 の StrictMode では useEffect が 2 回実行されるため、
  // API 呼び出しを 1 回だけに制限する（ログイン情報メールの二重送信を防止）
  const requestedRef = useRef(false);

  // 簡易登録＋自動ログイン（URL 進入時に 1 回だけ実行）
  const quickEntry = async () => {
    try {
      const res = await Axios.post('/api/trial/quick-entry', {
        email: emailFromUrl,
        campaignKey: campaignKey,
      });

      if (res.data.success) {
        // 自動ログイン（サーバーは HttpOnly クッキーも併せて設定する）
        window.localStorage.setItem('w_auth', res.data.w_auth);
        window.localStorage.setItem('w_authExp', res.data.w_authExp);

        // 現在の状態（仮ログイン）と、できること・できないことを案内する
        // ※ 遷移は案内モーダルの OK 押下時に行う
        showQuickEntryNotice(res.data.resent === true);
        return;
      }

      if (res.data.emailExists) {
        // 既に正式利用中のアカウント → ログイン画面へ
        props.history.replace(
          '/login?email=' + encodeURIComponent(emailFromUrl)
        );
        return;
      }

      // キャンペーン期間外・無効など
      setErrorMessage(res.data.message || GENERIC_ERROR_MESSAGE);
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error));
    }
  };

  useEffect(() => {
    // URLパラメータが不足している場合はAPIを呼ばない
    if (!emailFromUrl || !campaignKey) return;
    if (requestedRef.current) return;
    requestedRef.current = true;
    quickEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── エラー：URLパラメータ不足 ──
  if (!emailFromUrl || !campaignKey) {
    return (
      <div className="ds-trial-root">
        <div className="ds-trial-card">
          <h1 className="form-title">トライアルアカウント登録</h1>
          <div className="ds-trial-msg">
            {!emailFromUrl && <p>メールアドレスが指定されていません。</p>}
            {!campaignKey && <p>キャンペーンが指定されていません。</p>}
            <p>メールに記載されたリンクからアクセスしてください。</p>
          </div>
        </div>
      </div>
    );
  }

  // ── エラー：登録処理に失敗 ──
  // ※ 再試行ボタンは置かない（再試行がメールの再送を誘発するため）
  if (errorMessage) {
    return (
      <div className="ds-trial-root">
        <div className="ds-trial-card">
          <h1 className="form-title">トライアルアカウント登録</h1>
          <div className="ds-trial-msg" role="alert">
            <p>{errorMessage}</p>
            <p>メールに記載されたリンクを再度ご確認ください。</p>
          </div>
        </div>
      </div>
    );
  }

  // ── 処理中（既定の状態） ──
  return (
    <div className="ds-trial-root">
      <div className="ds-trial-card">
        <h1 className="form-title">トライアルアカウント登録</h1>
        <div className="ds-trial-loading">
          <Spin size="large" />
          <p className="ds-trial-loading-title">アカウントを準備しています…</p>
          <p className="ds-trial-loading-text">しばらくお待ちください。</p>
        </div>
      </div>
    </div>
  );
}

export default TrialAccountPage;
